import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Zap, Shield, Clock, Terminal, ArrowRight, ChevronRight, Rocket, Activity, GitBranch } from 'lucide-react';
import { Button } from '@/components/ui/button';

const features = [
  {
    icon: Terminal,
    title: 'Natural Language Deploy',
    description: 'Deploy with simple commands like "Deploy main to production" - AI understands your intent.'
  },
  {
    icon: Shield,
    title: 'AI Error Detective',
    description: 'Automatic root cause analysis and intelligent fix suggestions when issues occur.'
  },
  {
    icon: Activity,
    title: 'Performance Prophet',
    description: 'Predict how deployments will impact performance before they go live.'
  },
  {
    icon: Zap,
    title: 'Smart Code Review',
    description: 'AI-powered security and performance analysis focused on production readiness.'
  },
  {
    icon: Clock,
    title: 'Optimal Timing',
    description: 'AI recommends the best deployment windows based on traffic and team patterns.'
  },
  {
    icon: GitBranch,
    title: 'Visual Pipelines',
    description: 'Build deployment workflows visually with drag-and-drop simplicity.'
  }
];

const stats = [
  { value: '99.9%', label: 'Uptime' },
  { value: '45s', label: 'Avg Deploy' },
  { value: '10K+', label: 'Deployments' },
  { value: '85%', label: 'Less Incidents' }
];

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-[#0A0E27] overflow-hidden">
      {/* Scanlines overlay */}
      <div className="fixed inset-0 pointer-events-none scanlines opacity-30 z-50"></div>
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-40 bg-[#0A0E27]/80 backdrop-blur-md border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-primary to-secondary rounded flex items-center justify-center">
              <Rocket className="w-6 h-6 text-black" />
            </div>
            <span className="font-heading font-bold text-xl tracking-tight text-white">
              DeployForge<span className="text-primary">AI</span>
            </span>
          </Link>
          
          <div className="flex items-center gap-4">
            <Link to="/login">
              <Button variant="ghost" className="text-foreground/80 hover:text-primary" data-testid="nav-login-btn">
                Sign In
              </Button>
            </Link>
            <Link to="/register">
              <Button className="bg-primary text-black font-bold hover:shadow-neon-cyan" data-testid="nav-register-btn">
                Get Started
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6">
        {/* Background image with overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1573767291321-c0af2eaf5266?w=1920)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0E27] via-transparent to-[#0A0E27]" />
        
        <div className="relative max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <motion.div 
            className="lg:col-span-7"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/30 rounded-full mb-6">
              <span className="w-2 h-2 bg-accent rounded-full animate-pulse-glow"></span>
              <span className="text-sm text-primary font-medium">AI-Powered Deployment Platform</span>
            </div>
            
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              Deploy smarter.<br />
              <span className="text-glow-cyan text-primary">Ship faster.</span><br />
              Sleep better.
            </h1>
            
            <p className="text-lg text-foreground/70 mb-8 max-w-xl leading-relaxed">
              DeployForge AI transforms complex deployments into intelligent, streamlined experiences. 
              Your AI co-pilot for the entire deployment lifecycle.
            </p>
            
            <div className="flex flex-wrap gap-4">
              <Link to="/register">
                <Button 
                  size="lg" 
                  className="bg-primary text-black font-bold text-lg px-8 hover:shadow-neon-cyan transition-shadow"
                  data-testid="hero-get-started-btn"
                >
                  Start Deploying Free
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-secondary text-secondary hover:bg-secondary/10"
                data-testid="hero-demo-btn"
              >
                Watch Demo
              </Button>
            </div>
          </motion.div>
          
          <motion.div 
            className="lg:col-span-5"
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {/* Terminal preview */}
            <div className="bg-black/80 border border-primary/30 rounded-lg overflow-hidden shadow-neon-cyan">
              <div className="flex items-center gap-2 px-4 py-3 bg-[#151929] border-b border-border">
                <div className="w-3 h-3 rounded-full bg-destructive"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <div className="w-3 h-3 rounded-full bg-accent"></div>
                <span className="ml-3 text-xs text-muted-foreground font-code">deployforge-terminal</span>
              </div>
              <div className="p-6 font-code text-sm space-y-3">
                <div className="flex items-start gap-2">
                  <span className="text-primary">$</span>
                  <span className="text-foreground">deploy main to production</span>
                </div>
                <div className="text-muted-foreground pl-4 space-y-1">
                  <p className="text-accent">Analyzing code changes...</p>
                  <p>Code Quality Score: <span className="text-primary">94/100</span></p>
                  <p>Security Issues: <span className="text-accent">None found</span></p>
                  <p>Impact Score: <span className="text-primary">2/10</span> (Low)</p>
                  <p className="text-accent mt-2">Deploying to 5 servers...</p>
                  <p className="text-white mt-2">Deployment successful in 45s</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
        
        {/* Stats */}
        <motion.div 
          className="relative max-w-4xl mx-auto mt-20 grid grid-cols-2 md:grid-cols-4 gap-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          {stats.map((stat, i) => (
            <div key={i} className="text-center p-6 bg-card/50 border border-border rounded-lg">
              <div className="font-heading text-3xl font-bold text-primary text-glow-cyan mb-1">{stat.value}</div>
              <div className="text-sm text-muted-foreground">{stat.label}</div>
            </div>
          ))}
        </motion.div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6 bg-[#050714]">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
              AI Intelligence at <span className="text-secondary">Every Step</span>
            </h2>
            <p className="text-foreground/60 max-w-2xl mx-auto">
              From pre-deployment analysis to post-deployment monitoring, DeployForge AI provides intelligent assistance throughout your deployment lifecycle.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, i) => (
              <motion.div
                key={i}
                className="group p-6 bg-card/50 border border-border rounded-lg hover:border-primary/50 transition-colors"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                  <feature.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-heading font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-sm text-foreground/60 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-6">
              Ready to Transform Your Deployments?
            </h2>
            <p className="text-foreground/60 mb-8 max-w-xl mx-auto">
              Join thousands of developers who deploy with confidence using DeployForge AI.
            </p>
            <Link to="/register">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-primary to-secondary text-black font-bold text-lg px-10 hover:shadow-neon-cyan"
                data-testid="cta-get-started-btn"
              >
                Get Started Free
                <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-border">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Rocket className="w-5 h-5 text-primary" />
            <span className="font-heading font-bold text-foreground">DeployForge AI</span>
          </div>
          <p className="text-sm text-muted-foreground">
            © 2025 DeployForge AI. Deploy smarter. Ship faster. Sleep better.
          </p>
        </div>
      </footer>
    </div>
  );
}
